/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './Graph.scss';
import { isMobileOnly } from 'react-device-detect';
import { Container } from 'react-grid-system';
import { Chart as ChartJS, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Paper, Size, Button, ButtonType, Icon, BodyText, Heading } from '@vaisala-rockhopper/components';
import MainBanner from '../navigation/MainBanner';
import Footer from '../navigation/Footer';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import { CalculatorResult, ParameterModifier, ParameterName } from '../../../modules/calculator/calculatorTypes';
import CalculatorCardCollection from '../cards/CalculatorCardCollection';
import Annotation from 'chartjs-plugin-annotation';
import { ScrollNavContext } from '../../context/ScrollNavContext';
import PhaseIndicator from '../navigation/PhaseIndicator';
import { scrollToPhase } from '../navigation/ScrollNav';
import { ModalType } from '../modals/Modals';
import { OptionsConfig } from '../modals/OptionsModal';
import { handleScroll } from '../navigation/ScrollNav';
import { useTranslation } from 'react-i18next';
import { UnitPreset } from '../../../modules/units/unitTypes';
import { ReactComponent as InfoGraphIcon } from '../../../assets/icons/info_graph_icon.svg';
import { ReactComponent as InfoGraphAccentIcon } from '../../../assets/icons/info_graph_icon_accent.svg';
import _ from 'lodash';
import { clearTimeout } from 'timers';

interface GraphProps {
  activeMethod: number;
  graphActive: boolean;
  theme: string;
  setGraphActive: (value: boolean) => void;
  onParameterChange: (cardIndex: number, parameterName: ParameterName, value: ParameterModifier, phase?: number) => void;
  currentResult?: CalculatorResult;
  openModal: (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => void;
  currentUnitPreset: UnitPreset | undefined;
}

ChartJS.register(Filler, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, Annotation);

const formatLabels = (labels: any) => {
  return labels
    ? labels
        .map((item: any, i: number) => {
          return {
            ...item,
            index: i,
          };
        })
        .reverse()
        .filter((item: any, i: number, self: any) => {
          return self.findIndex((x: any) => x?.phase === item?.phase) === i;
        })
        .reverse()
    : [];
};

export const Graph = ({
  activeMethod,
  graphActive,
  theme,
  setGraphActive,
  onParameterChange,
  currentResult,
  openModal,
  currentUnitPreset,
}: GraphProps) => {
  const { t } = useTranslation();
  const [activePhase, setActivePhase] = React.useContext(ScrollNavContext);
  const chartRef = React.useRef();
  const [labelPositions, setLabelPositions] = React.useState<number[]>([]);

  const colors: any = {
    grid: {
      border: theme === 'vai-theme-light' ? 'rgba(207, 207, 207, 1.0)' : 'rgba(54, 54, 54, 1.0)',
    },
    h2o2Concentration: {
      border: theme === 'vai-theme-light' ? 'rgba(54, 54, 54, 1.0)' : 'rgba(228, 232, 237, 1.0)',
    },
    relativeSaturation: {
      border: theme === 'vai-theme-light' ? 'rgba(255, 150, 0, 0.8)' : 'rgba(255, 150, 0, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(255, 176, 26, 0.8)' : 'rgba(255, 176, 26, 0.8)',
    },
    relativeHumidity: {
      border: theme === 'vai-theme-light' ? 'rgba(0, 154, 200, 0.8)' : 'rgba(0, 103, 149, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(26, 180, 226, 0.8)' : 'rgba(0, 129, 175, 0.8)',
    },
    condensation: {
      border: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
      bg: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
    },
    activePhase: {
      bg: theme === 'vai-theme-light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },
    tooltip: {
      font: theme === 'vai-theme-light' ? 'rgba(54, 54, 54, 1.0)' : 'rgba(181, 181, 181, 1.0)',
      border: theme === 'vai-theme-light' ? 'rgba(207, 207, 207, 1)' : 'rgba(255, 255, 255, 0.25)',
      bg: theme === 'vai-theme-light' ? '#ffffff' : '#101719',
    },
  };
  const borderWidth = 2;
  const datasetSettings = {
    type: 'line' as const,
    fill: false,
    borderWidth: borderWidth,
    pointRadius: 2,
    pointBorderColor: 'rgba(0,0,0,0)',
    pointBackgroundColor: 'rgba(0,0,0,0)',
  };

  const graphData: any = React.useMemo(() => (currentResult ? currentResult.graphData : null), [currentResult]);
  const datasets: any = React.useMemo(() => (!_.isEmpty(graphData) ? graphData : { labels: [], data: [] }), [graphData]);
  const labels: any = React.useMemo(() => (datasets.labels ? datasets.labels : []), [datasets]);
  const formattedLabels: any = React.useMemo(() => formatLabels(labels), [labels]);

  React.useEffect(() => {
    const handleResize = () => {
      const chart: any = chartRef.current;
      if (chart) {
        const timeout = setTimeout(() => {
          const positions = formattedLabels.map((item: any) => {
            return chart.scales.x.getPixelForTick(item.index);
          });
          setLabelPositions(positions);
        }, 250);

        return () => clearTimeout(timeout);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [formattedLabels, chartRef]);

  const annotations = formattedLabels?.map((item: any, i: number) => {
    const phase = item.phase;
    const xMin = labels.findIndex((x: any) => x.phase === phase);
    const xMax = labels.length - [...labels].reverse().findIndex((x: any) => x.phase === phase);

    return {
      type: 'box',
      y1Min: 0,
      y1Max: 100,
      xMin: xMin,
      xMax: xMax,
      borderColor: 'rgba(0,0,0,0)',
      backgroundColor: phase === activePhase ? 'rgba(0,0,0,0)' : colors.activePhase.bg,
      click: () => {
        scrollToPhase(phase, setActivePhase, isMobileOnly ? true : false);
      },
    };
  });

  const annotationCondensation: any = {
    type: 'line',
    borderColor: colors.condensation.border,
    borderWidth: 4,
    label: {
      backgroundColor: colors.condensation.bg,
      content: 'Condensation',
      enabled: false,
      borderRadius: 100,
      padding: {
        top: 4,
        right: 8,
        left: 8,
        bottom: 4,
      },
      position: 'center' as const,
      yAdjust: -14,
      font: { size: '12px', weight: 'normal' },
    },
    scaleID: 'y1',
    value: 100,
    borderDash: [8, 8],
  };

  const options = {
    type: 'line',
    responsive: true,
    animation: {
      duration: 0,
    },
    stacked: true,
    events: ['click' as const],
    onClick: (e: any) => {},
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          padding: 16,
          boxWidth: 1,
          boxHeight: 8,
        },
      },
      annotation: {
        annotations: [annotations, annotationCondensation].flat(),
      },
      tooltip: {
        backgroundColor: colors.tooltip.bg,
        titleColor: colors.tooltip.font,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 14,
        },
        bodyColor: colors.tooltip.font,
        titleMarginBottom: 8,
        bodySpacing: 8,
        caretPadding: 16,
        padding: {
          top: 8,
          right: 8,
          bottom: 8,
          left: 8,
        },
        cornerRadius: 0,
        caretSize: 8,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        boxWidth: 0,
        boxHeight: 0,
      },
    },

    scales: {
      y: {
        min: 0,
        display: true,
        beginAtZero: true,
        position: 'left' as const,
        grid: {
          color: colors.grid.border,
        },
        ticks: {
          color: colors.grid.border,
        },
      },
      y1: {
        min: 0,
        max: 100,
        display: true,
        beginAtZero: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
          color: colors.grid.border,
        },
        ticks: {
          count: 5,
          maxTicksLimit: 5,
          color: colors.grid.border,
        },
      },
      x: {
        display: true,
        beginAtZero: true,
        grid: {
          color: colors.grid.border,
          display: false,
        },
        ticks: {
          display: false,
          color: colors.grid.border,
          autoSkip: false,
          callback: (value: any, i: any, ticks: any) => {
            const label = labels[i];
            return formattedLabels.find((x: any) => x.phase === label.phase && x.time === label.time) ? `${label.time} (${label.phase})` : '';
          },
        },
      },
    },
  };

  const data = {
    labels: labels.map((item: any, i: number) => {
      const time = new Date(item.time * 1000).toISOString().substring(11, 11 + 8);
      return `${time} (${item.phase})`;
    }),
    datasets: datasets?.data?.map((dataset: any) => {
      return {
        ...datasetSettings,
        borderColor: colors[dataset.label].border,
        backgroundColor: colors[dataset.label].bg,
        fill: colors[dataset.label].bg ? true : datasetSettings.fill,
        label: t(`param.${dataset.label}`),
        yAxisID: dataset.yAxisID,
        data: dataset.data,
      };
    }),
  };

  const yAxisTitle = (
    <div className="graph__y-axis-title-container">
      <div className="left">
        <BodyText>{t('ui.graph.header.h2o2Concentration')}</BodyText>
      </div>
      <div className="right">
        <div className="top">
          <BodyText>{t('ui.graph.header.humidity')}</BodyText>
        </div>
        <div className="bottom">
          <BodyText>{t('ui.graph.header.saturation')}</BodyText>
        </div>
      </div>
    </div>
  );

  const renderCustomLabels = () => {
    const labels = formattedLabels?.map((label: any, i: number, self: any) => {
      let isOverlapping = false;
      const labelBeforePosition = labelPositions[i - 1];
      const position = labelPositions[i];

      if (position - labelBeforePosition <= 50) {
        isOverlapping = true;
      }

      const time = new Date(label.time * 1000).toISOString().substring(11, 11 + 8);
      return (
        <div className="graph__custom-labels-button-wrap" key={i} style={{ left: position }}>
          <div>
            <span
              onClick={() => {
                scrollToPhase(label.phase, setActivePhase, isMobileOnly ? true : false);
              }}
            >
              <PhaseIndicator active={activePhase === label.phase} phase={label.phase} />
            </span>
            <span
              onClick={() => {
                scrollToPhase(label.phase, setActivePhase, isMobileOnly ? true : false);
              }}
            >
              <BodyText className={`${activePhase === label.phase ? 'is-active' : ''}`} style={{ marginTop: isOverlapping ? 12 : 0 }}>
                {time}
              </BodyText>
            </span>
          </div>
        </div>
      );
    });

    return (
      <div id="labels" className="graph__custom-labels">
        <div className="graph__custom-labels-wrap">{labels}</div>
      </div>
    );
  };

  const graphInfo = (
    <div className="graph__info">
      <Heading level="2">{t('ui.graph.info.rotateDevice')}</Heading>
      <div className="graph__info-item">
        {theme === 'vai-theme-light' ? <InfoGraphIcon /> : <InfoGraphAccentIcon />}
        <BodyText>{t('ui.graph.info.landscape')}</BodyText>
      </div>
    </div>
  );

  return isMobileOnly ? (
    <div className={`graph graph--mobile ${graphActive && activeMethod > -1 ? 'graph--active' : ''}  ${theme}`}>
      {graphInfo}
      <div className="graph__header">
        <Button
          buttonType={ButtonType.Link}
          buttonSize={Size.S}
          startIcon={<Icon name={VaiIcon.Cross} />}
          onClick={() => {
            setGraphActive(false);
          }}
        />
      </div>
      <div className="graph__grid">
        <div
          id="graph-card-container"
          className="graph__grid-item"
          onScroll={(e) => {
            handleScroll(e, setActivePhase, 'calculator-card--graph', graphActive);
          }}
        >
          <div className="graph__content">
            <CalculatorCardCollection
              variant="graph"
              onParameterChange={onParameterChange}
              currentResult={currentResult}
              activeMethod={activeMethod}
              openModal={openModal}
              currentUnitPreset={currentUnitPreset}
              theme={theme}
            />
          </div>
        </div>
        <div className="graph__grid-item">
          <div className="graph__content">
            {activeMethod === 0 && (
              <Paper>
                {yAxisTitle}
                {datasets?.data?.length > 0 && (
                  <div id="canvas">
                    <Line ref={chartRef} data={data} options={options} />
                  </div>
                )}
                {renderCustomLabels()}
              </Paper>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className={`graph graph--desktop ${activeMethod > -1 ? 'graph--active' : ''} ${theme}`}>
      <Container>
        <div className="graph__grid">
          <div className="graph__grid-item">
            {activeMethod === 0 && (
              <Paper className="graph__content">
                {yAxisTitle}
                {datasets?.data?.length > 0 && (
                  <div id="canvas">
                    <Line ref={chartRef} data={data} options={options} />
                  </div>
                )}
                {renderCustomLabels()}
              </Paper>
            )}
            <MainBanner variant="wide" />
            <div className="gtm-content gtm-content--desktop-calculator" />
            <Footer />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Graph;
